import {
  BanknotesIcon,
  PlayIcon,
  Squares2X2Icon,
  StopIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { WorkbenchInstancesMetadata } from "common";
import { classNames } from "@via/utils/helpers.tsx";

export type StatusBoxType =
  | "total"
  | "running"
  | "stopped"
  | "terminated"
  | "totalCost";

interface WorkbenchStatusBoxesProps {
  metadata?: WorkbenchInstancesMetadata;
  selectedStatus?: StatusBoxType | null;
}

interface StatusBoxConfig {
  type: StatusBoxType;
  label: string;
  icon: typeof PlayIcon;
  bgColor: string;
  iconColor: string;
  textColor: string;
  borderColor: string;
  value: string | number;
}

export default function WorkbenchStatusBoxes({
  metadata,
}: WorkbenchStatusBoxesProps) {
  const totals = metadata?.totals ?? {
    accessible: 0,
    running: 0,
    stopped: 0,
    terminated: 0,
    totalCost: null,
  };

  const formatCost = (cost: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cost);
  };

  const statusBoxes: StatusBoxConfig[] = [
    {
      type: "total",
      label: "Total Workbenches",
      icon: Squares2X2Icon,
      bgColor: "bg-indigo-50",
      iconColor: "text-indigo-600",
      textColor: "text-gray-900",
      borderColor: "border-indigo-200",
      value: totals.accessible,
    },
    {
      type: "running",
      label: "Running",
      icon: PlayIcon,
      bgColor: "bg-green-50",
      iconColor: "text-green-600",
      textColor: "text-gray-900",
      borderColor: "border-green-200",
      value: totals.running,
    },
    {
      type: "stopped",
      label: "Paused",
      icon: StopIcon,
      bgColor: "bg-gray-50",
      iconColor: "text-gray-600",
      textColor: "text-gray-900",
      borderColor: "border-gray-200",
      value: totals.stopped,
    },
    {
      type: "terminated",
      label: "Terminated",
      icon: XMarkIcon,
      bgColor: "bg-red-50",
      iconColor: "text-red-600",
      textColor: "text-gray-900",
      borderColor: "border-red-200",
      value: totals.terminated,
    },
    {
      type: "totalCost",
      label: "Total Cost",
      icon: BanknotesIcon,
      bgColor: "bg-purple-50",
      iconColor: "text-purple-600",
      textColor: "text-gray-900",
      borderColor: "border-purple-200",
      value: totals.totalCost ?? formatCost(0),
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 mb-6">
      {statusBoxes.map(box => {
        const Icon = box.icon;

        const boxContent = (
          <>
            <div className={`flex-shrink-0 ${box.iconColor}`}>
              <Icon className="h-8 w-8" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${box.textColor}`}>
                {box.label}
              </p>
              <p className={`text-2xl font-semibold ${box.textColor} mt-1`}>
                {box.value}
              </p>
            </div>
          </>
        );

        return (
          <div
            key={box.type}
            className={classNames(
              "relative flex items-center gap-4 rounded-lg border-2 p-4",
              "border-transparent",
              box.bgColor
            )}>
            {boxContent}
          </div>
        );
      })}
    </div>
  );
}
